import React, { useEffect, useState } from 'react';
import { Loader2, Save, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import RichTextEditor from '../../components/RichTextEditor';

const PUBLIC_PATH = { copyright: '/copyright-policy' };

const AdminLegal = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // slug
  const [form, setForm] = useState({ title: '', content: '', is_published: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/legal');
      setPages(r.data || []);
      if (!active && r.data?.length) select(r.data[0]);
    } catch (e) { toast({ title: 'Failed to load', description: errText(e) }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (p) => { setActive(p.slug); setForm({ title: p.title, content: p.content || '', is_published: p.is_published }); };

  const save = async () => {
    if (!form.title.trim()) return toast({ title: 'Title is required' });
    setSaving(true);
    try {
      await api.put(`/admin/legal/${active}`, form);
      toast({ title: 'Saved 🌸', description: 'Your policy page is live.' });
      load();
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      <div>
        <h1 className="font-serif-display text-3xl text-burgundy">Legal Pages</h1>
        <p className="text-mahogany/70 text-sm">Edit your policy pages with rich formatting. Paste your full document straight into the editor.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {pages.map((p) => (
          <button key={p.slug} onClick={() => select(p)} data-testid={`legal-tab-${p.slug}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${active === p.slug ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/60 text-burgundy border-rose/50 hover:border-rose'}`}>
            {p.title}
          </button>
        ))}
      </div>

      {active && (
        <div className="rounded-2xl border border-rose/40 bg-white/60 p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex-1 min-w-[240px]">
              <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Page title</span>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full bg-white border border-rose/50 rounded-lg px-3 py-2 text-burgundy" data-testid="legal-title-input" />
            </label>
            {PUBLIC_PATH[active] && (
              <Link to={PUBLIC_PATH[active]} target="_blank" className="pill-btn-outline text-sm py-2 mt-5">
                View page <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>

          <div>
            <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Policy content</span>
            <RichTextEditor value={form.content} onChange={(v) => setForm((f) => ({ ...f, content: v }))} placeholder="Paste or write the full policy here…" />
            <p className="text-[11px] text-mahogany/55 mt-1">Tip: paste from Word/Google Docs — formatting (headings, bold, lists, links) is preserved.</p>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-burgundy">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))} data-testid="legal-published" /> Published (visible to visitors)
            </label>
            <button onClick={save} disabled={saving} className="pill-btn" data-testid="legal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLegal;
